import { CardIcon, ErrorCircleIcon } from '../../icons';
import { Money } from '../Typography/Money';
import { formatServerMoney, MoneyType } from '@razz-team/utils';
import {
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemButtonProps,
  ListItemProps,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import { AvatarIcon } from '../Avatar/AvatarIcon';
import { addSeconds } from 'date-fns';
import { TimerChip } from '../Chip/TimerChip';
import { ClockIcon } from '@mui/x-date-pickers/icons';
import { useTranslation } from 'react-i18next';
import { AvatarBadge } from '../Avatar/AvatarBadge';

export type UiThreeDS = {
  amount: MoneyType;
  merchantInfo: {
    name: string;
  };
  state: 'PENDING' | 'APPROVED' | 'DECLINED';
  createdAt: string;
  lifeTimeInSec: number;
  lastFour: string;
};

function renderThreeDSListItemContent({ threeDS }: { threeDS: UiThreeDS }) {
  let icon: React.ReactNode = undefined;
  switch (threeDS.state) {
    case 'PENDING':
      icon = <ClockIcon />;
      break;
    case 'DECLINED':
      icon = <ErrorCircleIcon />;
      break;
  }

  return (
    <>
      <ListItemAvatar>
        <AvatarBadge type="icon" icon={icon}>
          <AvatarIcon icon={<CardIcon />} />
        </AvatarBadge>
      </ListItemAvatar>
      <ListItemText
        primary={<Money variant="body1" value={threeDS.amount} />}
        secondary={
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {threeDS.merchantInfo.name}
          </Typography>
        }
      />
      <ListItemSecondaryAction>
        {threeDS.state === 'PENDING' ? (
          <TimerChip endsAt={addSeconds(threeDS.createdAt, threeDS.lifeTimeInSec)} hideOnceEnded />
        ) : null}
      </ListItemSecondaryAction>
    </>
  );
}

export type ThreeDSListItemProps = ListItemProps<'li', { threeDS: UiThreeDS }>;

export function ThreeDSListItem({ threeDS, ...props }: ThreeDSListItemProps) {
  return <ListItem {...props}>{renderThreeDSListItemContent({ threeDS })}</ListItem>;
}

export type ThreeDSListItemButtonProps<RootComponent extends React.ElementType = 'div'> =
  ListItemButtonProps<RootComponent, { threeDS: UiThreeDS }>;

export function ThreeDSListItemButton<RootComponent extends React.ElementType = 'div'>({
  threeDS,
  ...props
}: ThreeDSListItemButtonProps<RootComponent>) {
  return <ListItemButton {...props}>{renderThreeDSListItemContent({ threeDS })}</ListItemButton>;
}

export function ThreeDSNotification({ threeDS }: { threeDS: UiThreeDS }) {
  const { t, i18n } = useTranslation();

  return t(`Approve card payment for {{amount}} at {{merchant}} using {{lastFour}} card`, {
    amount: formatServerMoney(threeDS.amount, { locale: i18n.language }),
    merchant: threeDS.merchantInfo.name,
    lastFour: threeDS.lastFour,
  });
}
